import React, { useEffect, useState } from "react";
import { api, brl } from "../../lib/api";
import { Link } from "react-router-dom";
import { ScoreBadge } from "../../components/Score";
import AlertCenter from "../../components/AlertCenter";
import { PortalPresenceCard } from "../../components/PortalPresenceCard";
import RankingPodium from "../../components/RankingPodium";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

const COLORS = ["#C5A566", "#7B9D7E", "#E2B77B", "#DDA7A5", "#8B5E5E"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [dist, setDist] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: o }, { data: d }] = await Promise.all([
          api.get("/reports/overview"),
          api.get("/reports/score-distribution"),
        ]);
        setData(o);
        setDist(d);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  if (!data) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]">Carregando painel...</div>;

  const fin = data.financial;
  const kpis = [
    { label: "Total Vendido", value: brl(fin.total_sold), test: "kpi-total-sold" },
    { label: "Total Recebido", value: brl(fin.total_received), test: "kpi-total-received" },
    { label: "Em Atraso", value: brl(fin.total_overdue), test: "kpi-total-overdue" },
    { label: "Taxa Inadimplencia", value: `${fin.default_rate}%`, test: "kpi-default-rate" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Painel administrativo</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Banco das Revendedoras</h1>
        </div>
        <div className="text-xs text-[#5C5A57]">
          {data.counts.clients} clientes · {data.counts.resellers} revendedoras · {data.counts.sales} vendas
        </div>
      </div>

      <AlertCenter />

      <PortalPresenceCard />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-10">
        {kpis.map((k) => (
          <div className="kpi-card" key={k.label} data-testid={k.test}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value tabular">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-[#EBE5DF] p-6 bg-white">
          <div className="flex items-center justify-between mb-6">
            <h2 className="section-title">Vendas por Revendedora</h2>
            <Link to="/admin/revendedoras" className="text-xs tracking-widest uppercase text-[#5C5A57] hover:text-black">Ver todas</Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.resellers.slice(0, 6)}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5C5A57" }} />
              <YAxis tick={{ fontSize: 11, fill: "#5C5A57" }} />
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE5DF" }} />
              <Bar dataKey="total_sold" fill="#0A0A0A" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#EBE5DF] p-6 bg-[#F5F0EB]">
          <h2 className="section-title mb-6">Distribuicao por Score</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={dist} dataKey="count" nameKey="classification" innerRadius={45} outerRadius={80}>
                {dist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #EBE5DF" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {dist.map((d, i) => (
              <div key={d.classification} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2">
                  <span className="dot" style={{ background: COLORS[i] }} /> {d.classification}
                </span>
                <span className="tabular font-medium">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title mb-4">Melhores Clientes</h2>
          <div className="space-y-3">
            {data.best_clients.map((c, i) => (
              <div key={`best-${i}-${c.name}`} className="flex items-center justify-between border-b border-[#EBE5DF] pb-3 last:border-0">
                <div className="text-sm">{c.name}</div>
                <div className="flex items-center gap-3">
                  <ScoreBadge score={c.score} />
                  <span className="text-xs text-[#5C5A57] tabular">{brl(c.credit_limit)}</span>
                </div>
              </div>
            ))}
            {data.best_clients.length === 0 && <div className="text-xs text-[#5C5A57]">Nenhum cliente cadastrado.</div>}
          </div>
        </div>
        <div className="border border-[#EBE5DF] bg-white p-6">
          <h2 className="section-title mb-4">Clientes de Risco</h2>
          <div className="space-y-3">
            {data.risk_clients.map((c, i) => (
              <div key={`risk-${i}-${c.name}`} className="flex items-center justify-between border-b border-[#EBE5DF] pb-3 last:border-0">
                <div className="text-sm">{c.name}</div>
                <ScoreBadge score={c.score} />
              </div>
            ))}
            {data.risk_clients.length === 0 && <div className="text-xs text-[#5C5A57]">Nenhum cliente de risco no momento.</div>}
          </div>
        </div>
      </div>

      <RankingPodium />
    </div>
  );
}
